import React from 'react';
import './ReportSourcesView.css';

// Interfaces
export interface DocumentDetails {
    _id: string;
    extracted_text: string;
    filename: string;
    url?: string;
    is_pdf?: boolean;
    content_ref?: string;
    token_size?: number;
    update_timestamp?: string;
    extracted_timestamp?: string;
    checksum?: string;
    extraction_error?: string;
    download_timestamp?: string;
    metadata?: {
        title?: string;
        author?: string;
        date?: string;
        document_type?: string;
        language?: string;
        subject?: string;
        keywords?: string[];
    };
    metadata_validation_details?: {
        title?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        author?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        date?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        keywords?: {
            field: string;
            value: string[];
            found: boolean;
            confidence: number;
            matches?: any[];
        };
        subject?: {
            field: string;
            value: string;
            found: boolean;
            confidence: number;
            matches?: any[];
        };
    };
    metadata_validated?: string;
}

export interface SearchResultEntity {
    text?: string;
    document_id?: string;
    page?: number | string;
}

export interface SearchResult {
    _id?: string;
    entity: SearchResultEntity;
    distance?: number;
    score?: number;
    source?: {
        _id: string;
        name: string;
        filename?: string;
        url?: string;
        token_size?: number;
        is_pdf?: boolean;
    };
}

interface DocumentListItem {
    _id: string;
    url?: string;
    filename?: string;
}

export interface DocumentDetailViewProps {
    documentId: string;
    documentTitle?: string;
    documentDetails?: DocumentDetails;
    documentText?: string;
    documentUrl?: string;
    isPdf?: boolean;
    documentChunks: SearchResult[];
    loadingDocument: boolean;
    clearSelectedDocument: () => void;
    allDocuments: DocumentListItem[];
    onSelectDocument: (docId: string) => void;
    searchQuery?: string;
}

const DocumentDetailView: React.FC<DocumentDetailViewProps> = ({
    documentId,
    documentTitle = '',
    documentDetails,
    documentText = '',
    documentUrl = '',
    isPdf = false,
    documentChunks = [],
    loadingDocument = false,
    clearSelectedDocument,
    allDocuments = [],
    onSelectDocument,
    searchQuery = ''
}) => {
    // Function to highlight the search query terms in the text
    const highlightMatches = (text: string) => {
        if (!searchQuery || !text) return text;
        
        // First, try to find exact matches of the whole query
        interface ExactMatch {
            index: number;
            length: number;
            term: string;
        }
        
        const exactMatches: ExactMatch[] = [];
        if (searchQuery.length > 3) {
            const exactRegex = new RegExp(escapeRegExp(searchQuery), 'gi');
            let exactMatch;
            while ((exactMatch = exactRegex.exec(text)) !== null) {
                exactMatches.push({
                    index: exactMatch.index,
                    length: searchQuery.length,
                    term: searchQuery
                });
            }
        }
        
        // Split the search query into individual terms, preserve numbers with commas/decimals
        const searchTerms = searchQuery
            .toLowerCase()
            .split(/\s+/)
            .filter(term => {
                // Include terms with at least 3 characters or numeric values
                return term.length >= 3 || /[\d.,]+/.test(term);
            });
        
        if (searchTerms.length === 0 && exactMatches.length === 0) return text;
        
        // First split the text by newlines to preserve formatting
        const lines = text.split('\n');
        
        return lines.map((line, lineIndex) => {
            // Check for exact matches in this line
            const lineExactMatches = exactMatches.filter(match => {
                const matchPos = match.index;
                // Calculate offset in the original text
                let offset = 0;
                for (let i = 0; i < lineIndex; i++) {
                    offset += lines[i].length + 1; // +1 for the newline character
                }
                // Check if the match is within this line
                return matchPos >= offset && matchPos < offset + line.length;
            });
            
            // Create a regex pattern that handles numbers with commas and decimals
            const numericPattern = searchTerms
                .map(term => {
                    // Special handling for numeric terms
                    if (/^[\d.,]+$/.test(term)) {
                        // Escape the term for regex to handle commas and dots as literals
                        return escapeRegExp(term);
                    }
                    return term;
                })
                .join('|');
            
            // If no search terms after filtering, just return the line
            if (numericPattern === '') {
                if (lineExactMatches.length > 0) {
                    // Handle exact matches
                    let lastIndex = 0;
                    const parts = [];
                    
                    // Sort exact matches by index
                    lineExactMatches.sort((a, b) => {
                        // Calculate adjusted indices for this line
                        let offsetA = a.index;
                        let offsetB = b.index;
                        for (let i = 0; i < lineIndex; i++) {
                            offsetA -= lines[i].length + 1;
                            offsetB -= lines[i].length + 1;
                        }
                        return offsetA - offsetB;
                    });
                    
                    // Process exact matches
                    for (const match of lineExactMatches) {
                        // Calculate adjusted index for this line
                        let adjustedIndex = match.index;
                        for (let i = 0; i < lineIndex; i++) {
                            adjustedIndex -= lines[i].length + 1;
                        }
                        
                        // Add text before the match
                        if (adjustedIndex > lastIndex) {
                            parts.push(<span key={`${lineIndex}-before-${adjustedIndex}`}>{line.substring(lastIndex, adjustedIndex)}</span>);
                        }
                        
                        // Add highlighted match
                        parts.push(
                            <mark key={`${lineIndex}-match-${adjustedIndex}`} className="highlighted-text">
                                {line.substring(adjustedIndex, adjustedIndex + match.term.length)}
                            </mark>
                        );
                        
                        lastIndex = adjustedIndex + match.term.length;
                    }
                    
                    // Add text after the last match
                    if (lastIndex < line.length) {
                        parts.push(<span key={`${lineIndex}-after-${lastIndex}`}>{line.substring(lastIndex)}</span>);
                    }
                    
                    return (
                        <React.Fragment key={`line-${lineIndex}`}>
                            {parts}
                            {lineIndex < lines.length - 1 && <br />}
                        </React.Fragment>
                    );
                }
                
                return (
                    <React.Fragment key={`line-${lineIndex}`}>
                        {line}
                        {lineIndex < lines.length - 1 && <br />}
                    </React.Fragment>
                );
            }
            
            const pattern = new RegExp(`(${numericPattern})`, 'gi');
            
            // Split the line based on the pattern
            const parts = line.split(pattern);
            
            // Map parts to React elements with highlights
            const highlightedParts = parts.map((part, i) => {
                // Check if this part matches any search term
                const isMatch = searchTerms.some(term => 
                    part.toLowerCase() === term.toLowerCase()
                );
                
                return isMatch ? 
                    <mark key={`${lineIndex}-${i}`} className="highlighted-text">{part}</mark> : 
                    <span key={`${lineIndex}-${i}`}>{part}</span>;
            });
            
            // Return the line with highlights, with a line break after (except for the last line)
            return (
                <React.Fragment key={`line-${lineIndex}`}>
                    {highlightedParts}
                    {lineIndex < lines.length - 1 && <br />}
                </React.Fragment>
            );
        });
    };

    // Helper function to escape special characters in regex
    const escapeRegExp = (string: string) => {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    return (
        <div className="document-detail-view">
            <div className="document-detail-layout">
                {/* Navigation sidebar */}
                <div className="document-navigation-sidebar">
                    <h3>Found Documents</h3>
                    <div className="document-list">
                        {allDocuments.map((doc) => (
                            <div 
                                key={doc._id}
                                className={`document-list-item ${doc._id === documentId ? 'active' : ''}`}
                                onClick={() => onSelectDocument(doc._id)}
                            >
                                <div className="document-list-name">
                                    {doc.filename 
                                        ? (doc.filename.length > 10 
                                            ? `...${doc.filename.slice(-10)}` 
                                            : doc.filename)
                                        : 'Unnamed Document'
                                    }
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                
                {/* Main content area */}
                <div className="document-detail-container">
                    <div className="document-header">
                        <div className="document-actions" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <button
                              className="back-button primary-button"
                              onClick={clearSelectedDocument}
                            >
                                ← Back
                            </button>
                            <a
                              href={documentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="download-link"
                            >
                                Download
                            </a>
                        </div>
                    </div>

                    {/* Metadata section (collapsible) */}
                    {documentDetails && (
                        <div className="metadata-section">
                            <div className="metadata-grid">
                                <div className="metadata-item">
                                    <h4>Document ID</h4>
                                    <p>{documentDetails._id}</p>
                                </div>
                                
                                {/* Document Title with confidence */}
                                {documentDetails.metadata?.title && (
                                    <div className="metadata-item">
                                        <h4>Document Title</h4>
                                        <p>
                                            {documentDetails.metadata.title}
                                            {documentDetails.metadata_validation_details?.title?.confidence && (
                                                <span 
                                                    className="confidence-badge" 
                                                    title={`Confidence: ${(documentDetails.metadata_validation_details.title.confidence * 100).toFixed(1)}%`}
                                                    style={{
                                                        marginLeft: '8px',
                                                        fontSize: '0.8em',
                                                        backgroundColor: documentDetails.metadata_validation_details.title.confidence >= 0.9 ? '#28a745' : documentDetails.metadata_validation_details.title.confidence >= 0.7 ? '#ffc107' : '#dc3545',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {(documentDetails.metadata_validation_details.title.confidence * 100).toFixed(0)}%
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}
                                
                                {/* Document Author with confidence */}
                                {documentDetails.metadata?.author && (
                                    <div className="metadata-item">
                                        <h4>Author</h4>
                                        <p>
                                            {documentDetails.metadata.author}
                                            {documentDetails.metadata_validation_details?.author?.confidence && (
                                                <span 
                                                    className="confidence-badge" 
                                                    title={`Confidence: ${(documentDetails.metadata_validation_details.author.confidence * 100).toFixed(1)}%`}
                                                    style={{
                                                        marginLeft: '8px',
                                                        fontSize: '0.8em',
                                                        backgroundColor: documentDetails.metadata_validation_details.author.confidence >= 0.9 ? '#28a745' : documentDetails.metadata_validation_details.author.confidence >= 0.7 ? '#ffc107' : '#dc3545',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {(documentDetails.metadata_validation_details.author.confidence * 100).toFixed(0)}%
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}
                                
                                {/* Document Date with confidence */}
                                {documentDetails.metadata?.date && (
                                    <div className="metadata-item">
                                        <h4>Document Date</h4>
                                        <p>
                                            {documentDetails.metadata.date}
                                            {documentDetails.metadata_validation_details?.date?.confidence && (
                                                <span 
                                                    className="confidence-badge" 
                                                    title={`Confidence: ${(documentDetails.metadata_validation_details.date.confidence * 100).toFixed(1)}%`}
                                                    style={{
                                                        marginLeft: '8px',
                                                        fontSize: '0.8em',
                                                        backgroundColor: documentDetails.metadata_validation_details.date.confidence >= 0.9 ? '#28a745' : documentDetails.metadata_validation_details.date.confidence >= 0.7 ? '#ffc107' : '#dc3545',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {(documentDetails.metadata_validation_details.date.confidence * 100).toFixed(0)}%
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}
                                
                                {/* Document Type */}
                                {documentDetails.metadata?.document_type && (
                                    <div className="metadata-item">
                                        <h4>Document Type</h4>
                                        <p>{documentDetails.metadata.document_type}</p>
                                    </div>
                                )}
                                
                                {/* Language */}
                                {documentDetails.metadata?.language && (
                                    <div className="metadata-item">
                                        <h4>Language</h4>
                                        <p>{documentDetails.metadata.language}</p>
                                    </div>
                                )}
                                
                                {/* Subject with confidence */}
                                {documentDetails.metadata?.subject && (
                                    <div className="metadata-item">
                                        <h4>Subject</h4>
                                        <p>
                                            {documentDetails.metadata.subject}
                                            {documentDetails.metadata_validation_details?.subject?.confidence && (
                                                <span 
                                                    className="confidence-badge" 
                                                    title={`Confidence: ${(documentDetails.metadata_validation_details.subject.confidence * 100).toFixed(1)}%`}
                                                    style={{
                                                        marginLeft: '8px',
                                                        fontSize: '0.8em',
                                                        backgroundColor: documentDetails.metadata_validation_details.subject.confidence >= 0.9 ? '#28a745' : documentDetails.metadata_validation_details.subject.confidence >= 0.7 ? '#ffc107' : '#dc3545',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {(documentDetails.metadata_validation_details.subject.confidence * 100).toFixed(0)}%
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}
                                
                                {/* Keywords with confidence */}
                                {documentDetails.metadata?.keywords && documentDetails.metadata.keywords.length > 0 && (
                                    <div className="metadata-item">
                                        <h4>Keywords</h4>
                                        <p>
                                            {documentDetails.metadata.keywords.join(', ')}
                                            {documentDetails.metadata_validation_details?.keywords?.confidence && (
                                                <span 
                                                    className="confidence-badge" 
                                                    title={`Confidence: ${(documentDetails.metadata_validation_details.keywords.confidence * 100).toFixed(1)}%`}
                                                    style={{
                                                        marginLeft: '8px',
                                                        fontSize: '0.8em',
                                                        backgroundColor: documentDetails.metadata_validation_details.keywords.confidence >= 0.9 ? '#28a745' : documentDetails.metadata_validation_details.keywords.confidence >= 0.7 ? '#ffc107' : '#dc3545',
                                                        color: 'white',
                                                        padding: '2px 6px',
                                                        borderRadius: '12px',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {(documentDetails.metadata_validation_details.keywords.confidence * 100).toFixed(0)}%
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                )}
                                
                                {documentDetails.filename && (
                                    <div className="metadata-item">
                                        <h4>Filename</h4>
                                        <p>{documentDetails.filename}</p>
                                    </div>
                                )}
                                
                                {documentDetails.token_size !== undefined && (
                                    <div className="metadata-item">
                                        <h4>Token Size</h4>
                                        <p>{documentDetails.token_size.toLocaleString()} tokens</p>
                                    </div>
                                )}
                                
                                {documentDetails.content_ref && (
                                    <div className="metadata-item">
                                        <h4>Content Reference</h4>
                                        <p>{documentDetails.content_ref}</p>
                                    </div>
                                )}
                                {documentDetails.update_timestamp && (
                                    <div className="metadata-item">
                                        <h4>Updated</h4>
                                        <p>{new Date(documentDetails.update_timestamp).toLocaleString()}</p>
                                    </div>
                                )}
                                {documentDetails.extracted_timestamp && (
                                    <div className="metadata-item">
                                        <h4>Extracted</h4>
                                        <p>{new Date(documentDetails.extracted_timestamp).toLocaleString()}</p>
                                    </div>
                                )}
                                {documentDetails.download_timestamp && (
                                    <div className="metadata-item">
                                        <h4>Downloaded</h4>
                                        <p>{new Date(documentDetails.download_timestamp).toLocaleString()}</p>
                                    </div>
                                )}
                                {documentDetails.is_pdf !== undefined && (
                                    <div className="metadata-item">
                                        <h4>File Type</h4>
                                        <p>{documentDetails.is_pdf ? 'PDF Document' : 'Web Document'}</p>
                                    </div>
                                )}
                                {documentDetails.checksum && (
                                    <div className="metadata-item">
                                        <h4>Checksum</h4>
                                        <p>{documentDetails.checksum}</p>
                                    </div>
                                )}
                                {documentDetails.extraction_error && (
                                    <div className="metadata-item error">
                                        <h4>Extraction Error</h4>
                                        <p>{documentDetails.extraction_error}</p>
                                    </div>
                                )}
                                {documentDetails.url && (
                                    <div className="metadata-item">
                                        <h4>Source URL</h4>
                                        <a 
                                            href={documentDetails.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            {documentDetails.url}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="document-content-wrapper">
                        <div className="document-source-panel">
                            {loadingDocument ? (
                                <div className="loading-indicator">Loading document...</div>
                            ) : (
                                <>
                                    {isPdf && documentUrl && (
                                        <iframe 
                                            className="document-iframe" 
                                            src={documentUrl} 
                                            title={documentTitle}
                                        />
                                    )}
                                    {!isPdf && documentText && (
                                        <div className="document-text-content">
                                            <pre>{highlightMatches(documentText)}</pre>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        <div className="document-chunks-panel">
                            <h3>Document Chunks</h3>
                            <div className="chunks-list">
                                {documentChunks.length > 0 ? (
                                    documentChunks.map((chunk, index) => (
                                        <div key={index} className="chunk-item">
                                            <div className="chunk-content">
                                                {highlightMatches(chunk.entity.text || "")}
                                            </div>
                                            {chunk.entity.page && (
                                                <div className="chunk-page">
                                                    Page: {chunk.entity.page}
                                                </div>
                                            )}
                                            {chunk.distance !== undefined && (
                                                <div className="chunk-relevance">
                                                    Score: {(1 - chunk.distance).toFixed(4)}
                                                </div>
                                            )}
                                            {chunk.score !== undefined && (
                                                <div className="chunk-relevance">
                                                    Score: {chunk.score.toFixed(4)}
                                                </div>
                                            )}
                                        </div>
                                    ))
                                ) : (
                                    <div className="no-chunks-message">
                                        No chunks available for this document.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocumentDetailView;
